import VacancyInterface from './Vacancy';
import AreaInterface from './Area';
import RefereeSourceInterface from './RefereeSource';
import normalizeVacancy from './normalize-vacancies';

import CityInterface from './City';
import Option from '../common/components/Select/Option';

import client from './ApolloClient';
import { gql } from '@apollo/client';


interface DataInterface {
  vacancies: Array<VacancyInterface>,
  company: string,
  cities: Array<CityInterface>,
  areas: Array<AreaInterface>,
  refereeSources: Array<RefereeSourceInterface>,
}

interface VacancyFilter {
  id?: string | undefined;
  name?: string | undefined;
  area?: string | undefined;
  department?: string | undefined;
  city?: string | undefined;
  [key: string]: string | number | undefined;
}

export default class Data {

  public hasStoredData: boolean;
  public isDataLoaded: boolean;
  /**
   * singleton instance
   */
  private static instance: Data;

  /**
   * Site data
   */
  private data: DataInterface;

  static async load(): Promise<{data: DataInterface}> {
    return await client.query({query: gql`
      query {
        vacancies(where: {hidden:false}) {
          id,
          name,
          uri_alias,
          description,
          min_experience,
          has_team_success_tab,
          max_experience,
          cities{id},
          area{id},
          main_tabs {
            id,
            title,
            content {
              id,
              description
            }
          },
          tabs,
          is_referal,
          departament{id, name}
        },
        cities(sort: "weight:desc") {
          id,
          name,
          weight,
          uri_alias,
          name_prepositional
        },
        areas(sort: "weight:desc") {
          id,
          weight,
          uri_alias,
          name
        },
        refereeSources {
          id,
          name
        }
      }
    `})
  }

  public static async getVacancyReferalBridge(): Promise<{id: string} | undefined> {
    const data = await client.query({query: gql`
      query {
        vacancies(where: {is_referal_bridge: true}) {
          id
        }
      }
    `});
    return data.data.vacancies[0];
  }

  constructor() {
    this.data = {
      cities: [],
      areas: [],
      refereeSources: [],
      vacancies: [],
      company: "UCHi.ru"
    }
    this.hasStoredData = false;
    this.isDataLoaded = false;
  }

  public update(data: DataInterface): void {
    this.data = {...this.data, ...data};

    if (Object.values(data).length > 0) this.isDataLoaded = true;

    if (data.vacancies.length > 0) {
      const normalizedVacancies = data.vacancies.map((v: any) => normalizeVacancy(v));
      this.data.vacancies = normalizedVacancies;
    }
  }


  /**
   * Возвращает массив всех доступных городов
   */
  public get cities(): Array<CityInterface> {
    return this.data.cities;
  }

  /**
   * Возвращает массив всех доступных городов с вакансиями
   * сортированный по убыванию веса города
   */
  public get citiesWithVacancies(): Array<CityInterface> {
    const cities: Set<CityInterface> = new Set();
    this.data.vacancies.forEach((v) => {
      v.cities.forEach((c) => cities.add(this.cityById(c.id)))
    });
    return Array.from(cities).sort((a, b) => b.weight - a.weight);
  }

  /**
   * Возвращает массив всех доступных городов
   */
  public citiesAsOptions(cities: Array<CityInterface>): Array<Option> {
    return cities.map((city) => {
      return {key: city.id, value: city.name_prepositional }
    });
  }

  /**
   * Возвращает человекочитаемое имя города по идентификатору
   */
  public cityName(id: string): string {
    return this.cityById(id).name;
  }

  /**
   * Возвращает человекочитаемое имя города по идентификатору
   */
  public cityById(id: string): CityInterface {
    return this.data.cities.filter((c) => c.id === id)[0];
  }

  /**
   * Возвращает человекочитаемое имя города по uri_alias
   */
  public cityByAlias(alias: string): CityInterface {
    return this.data.cities.filter((c) => c.uri_alias === alias)[0];
  }

  /**
   * Возвращает человекочитаемое имя города по идентификатору
   */
  public cityNamePrepositional(id: string): string {
    return this.data.cities.filter((c) => c.id === id)[0].name_prepositional;
  }

  public get areas(): Array<AreaInterface> {
    return this.data.areas;
  }

  /**
   * отдает области деятельности, для существующих вакансий по id города,
   * @param  city идентификатор города (напрмер "1", строкой приходит с gql
   *              поэтому и тут строка)
   */
  public areasByCity(cityId: string): Array<AreaInterface> {
    const result = this.data.vacancies
      .filter((v: VacancyInterface) => v.cities.some((o) => o.id === cityId))
      .map((v: VacancyInterface) => this.area(v.area))
      .filter((a: AreaInterface, index: number, self: AreaInterface[]) => a && (self.indexOf(a) === index))
      .sort((a1, a2) => a2.weight - a1.weight);
    return result;
  }

  /**
   * отдает первый инстанц областей деятельности по uri_alias (должен быть толко один
   * так как uri_alias - уникальнео поле)
   * @param  uri_alias - алиас города, уникальное поле в базе
   */
  public areaByAlias(uri_alias: string): AreaInterface {
    return this.data.areas.filter((a) =>  a.uri_alias === uri_alias)[0];
  }

  public areaName(id: string): string {
    return this.area(id).name;
  }

  public area(id: string): AreaInterface {
    return this.data.areas.filter((a) =>  a.id === id)[0];
  }


  /**
   * Apply filters to vacancies list
   * @param  filter {VacancyFilter} object where keys should match Vacancy field
   * @return        filtered vacancies list
   */
  public vacancies(filter?: VacancyFilter): Array<VacancyInterface> {
    if (!filter) return this.data.vacancies;

    const filterKeys = Object.keys(filter);
    const cityKeyIndex = filterKeys.indexOf('city', 0);

    // NOTE: separate check for city, because city is an array of objects
    let vacancies = filter.city
    ? this.vacanciesByCity(filter.city)
    : this.data.vacancies;

    if (cityKeyIndex > -1) filterKeys.splice(cityKeyIndex, 1);

    // NOTE: filter by other keys
    filterKeys.forEach((key) => {
      vacancies = vacancies.filter((v) => {
        return v[key as keyof VacancyInterface] === filter[key];
      });
    });
    return vacancies;
  }

  public vacanciesByCity(cityId: string): Array<VacancyInterface> {
    return this.data.vacancies.filter((v) => v.cities.some((c) => c.id === cityId));
  }

  public vacanciesReferal(): Array<VacancyInterface> {
    return this.data.vacancies.filter((v) => v.is_referal);
  }


  public getVacancy(id: string): VacancyInterface {
    const vacanciesArray = this.vacancies({'id': id});
    return vacanciesArray[0];
  }


  public getVacancyByAlias(alias: string): VacancyInterface {
    const vacanciesArray = this.vacancies({'uri_alias': alias});
    return vacanciesArray[0];
  }

  public refereeSources(): Array<RefereeSourceInterface> {
    return this.data.refereeSources;
  }

  public refereeSourceById(id: string): RefereeSourceInterface {
    return this.data.refereeSources.filter((s) => s.id === id)[0];
  }


  public get company(): string {
    return this.data.company;
  }

  /**
   * returns singleton
   */
  public static get Instance(): Data {
    if (!this.instance) this.instance = new this();
    return this.instance;
  }

}
